import React, { forwardRef, Ref, useImperativeHandle } from 'react';
import TableTransfer from '../TableTransfer';
import { AntdColumn, AntdColumnUtilType } from '../../../types/common/table';
import { antdTableSimpleColumns } from '../../../lib/utils/tableUtils';
import { SimpleTableTransferStyle } from './style';

interface SimpleTableTransferProps<Item> {
  dataSource: Item[];
  value?: Item[];
  leftTitle: string;
  rightTitle: string;
  leftColumns: (utils: AntdColumnUtilType<Item, Record<string, unknown>>) => AntdColumn<Item>[];
  rightColumns: (utils: AntdColumnUtilType<Item, Record<string, unknown>>) => AntdColumn<Item>[];
  onChange?: (value: Item[]) => void;
  rowClassName?: (record: Item, index: number) => string | null;
}

const SimpleTableTransferInner = <Item extends { id: number; invalid?: boolean }>(
  props: SimpleTableTransferProps<Item>,
  ref: Ref<{ value?: Item[]; onChange?: (value: Item[]) => void }>,
): JSX.Element => {
  const { dataSource, value = [], leftTitle, rightTitle, leftColumns, rightColumns, onChange, rowClassName } = props;

  useImperativeHandle(ref, () => ({
    value,
    onChange,
  }));

  const setData = (values: Item[]): void => onChange && onChange(values);

  const handleChange = (action: 'add' | 'remove', moveIdList: number[]): void => {
    if (action === 'add') {
      const targetList = dataSource.filter((data) => moveIdList.includes(data.id));
      setData([...targetList, ...value]);
    } else if (action === 'remove') {
      const targetList = value.filter((item) => !moveIdList.includes(item.id));
      setData(targetList);
    }
  };

  return (
    <SimpleTableTransferStyle>
      <TableTransfer
        onChange={handleChange}
        dataSource={dataSource}
        operations={['등록', '해제']}
        leftTitle={leftTitle}
        rightTitle={rightTitle}
        targetKeys={value.map((item) => item.id)}
        leftTableProps={{
          columns: (utils: AntdColumnUtilType<Item, Record<string, unknown>>): AntdColumn<Item>[] =>
            antdTableSimpleColumns(leftColumns(utils)),
          rowClassName,
        }}
        rightTableProps={{
          columns: (utils: AntdColumnUtilType<Item, Record<string, unknown>>): AntdColumn<Item>[] =>
            antdTableSimpleColumns(rightColumns(utils)),
          rowClassName,
          canDrag: true,
          onDragEnd: ({ list }: { list: Item[] }): void => setData(list),
          list: value,
        }}
      />
    </SimpleTableTransferStyle>
  );
};

//필터와 정렬 없는 TableTransfer
const SimpleTableTransfer = forwardRef(SimpleTableTransferInner) as <Item extends { id: number; invalid?: boolean }>(
  props: SimpleTableTransferProps<Item>,
  ref: Ref<{ value?: Item[]; onChange?: (value: Item[]) => void }>,
) => JSX.Element;

export default SimpleTableTransfer;
